import { Capacitor } from '@capacitor/core';
import { Haptics, NotificationType } from '@capacitor/haptics';
import { PurchasesPackage } from '@revenuecat/purchases-capacitor';
import { promiseTimeout } from '@vueuse/shared';

import { alerts, createAlert } from '../../shared/alerts.ts';
import { useApiRequest } from '../../shared/composables/request.ts';
import { Product } from '../../shared/models/product.ts';
import { confetti } from '../../shared/utils/animate.ts';

export type RevenueCatPackageIdentifier =
    | '$rc_monthly'
    | '$rc_three_month'
    | '$rc_six_month'
    | '$rc_annual';

export interface RevenueCatPackage {
    identifier: RevenueCatPackageIdentifier;
    platformIdentifier: string;
    price: number;
    currency: string;
    messages?: any;
    package?: PurchasesPackage;
}

export const revenueCatPackageMonths: Record<RevenueCatPackageIdentifier, number> = {
    $rc_monthly: 1,
    $rc_three_month: 3,
    $rc_six_month: 6,
    $rc_annual: 12,
};

export interface PaymentsProvider {
    getPackages(offeringId: string): Promise<RevenueCatPackage[] | undefined>;
    purchase(pkg: RevenueCatPackage, product: Product): Promise<boolean>;
}

let paymentsProvider: PaymentsProvider = {
    async getPackages() {
        return undefined;
    },
    async purchase() {
        return false;
    },
};

export function providePaymentsProvider(provider: PaymentsProvider) {
    paymentsProvider = provider;
}

export function usePaymentsProvider() {
    return paymentsProvider;
}

async function celebrateSubscription() {
    const alert = createAlert({
        type: 'success',
        message: 'Thanks for subscribing to eMedici!',
    });

    alerts.show(alert, { key: 'subscribed' });

    if (Capacitor.getPlatform() === 'ios') {
        Haptics.notification({ type: NotificationType.Success }).catch(() => {});
    }

    await promiseTimeout(500);

    confetti(alert, {
        duration: 5000,
        angle: -90,
        spread: 180,
        startVelocity: 30,
    });
}

/**
 * Celebrate once a completed payment has granted or extended access.
 *
 * Payment can complete before our webhook updates the subscription, so this
 * polls until the product has a paid subscription whose expiry differs from
 * the one before purchase. Paid months are added onto any current expiry, so
 * this also confirms a purchase that converts a trial. If access hasn't landed
 * in time, the customer is told their payment was received so they don't pay
 * a second time.
 */
export async function confirmSubscription(productId: string, previousExpiry?: string | null) {
    try {
        for (let attempt = 0; attempt < 10; attempt++) {
            if (attempt) await promiseTimeout(2000);

            const request = await useApiRequest<Product>(
                `products/${encodeURIComponent(productId)}?include=subscription&fields[products]=subscription`,
            );

            const subscription = request.model.value?.subscription;
            if (
                subscription?.isActive &&
                !subscription.isTrial &&
                subscription.expiresAt !== previousExpiry
            ) {
                void celebrateSubscription();
                return;
            }
        }
    } catch {
        // Payment has succeeded even if access confirmation is unavailable.
    }

    alerts.show(
        createAlert({
            type: 'info',
            message:
                'Your payment was received, but we’re still confirming your access. Please don’t purchase again. Check back shortly, and contact support if your access is still missing.',
        }),
    );
}
